import { Dropdown, SubmitButton, TextInput } from '@components/common';
import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useValidation } from '@hooks/validations';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import '@assets/styles/pages/addNewMeetings.scss';
import {
	defaultAddMdpMeetingInitialValues,
	IDefaultAddMdpMeetingFormInitialValues,
	SocialMeetType,
	PRODUCT_SPECIFIC_MEETS,
} from '.';
import { monthsOptions, keyDownEvent } from '@helper/utils';
import { useGetRxDb } from '@hooks/getRxdbData';
import { useLocation, useNavigate } from 'react-router-dom';
import MultiselectDropdown from '@components/common/formControl/multiselect-dropdown/multiselectDropdown';
import { useMeetingsTypesQuery } from '@services/hooks/meetings/useMeetingTypesQuery';
import { MDPMeetingDetails, MeetingType, RBDMClubDropdownItem } from '@services/hooks/meetings';
import { ToastType, useTosterStore } from '@store/toster';
import { useGetMeetingQuery } from '@services/hooks/meetings/useGetMeetingQuery';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useIMRCodeListQuery } from '@services/hooks/enduser-details/useIMRCodeListQuery';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { CMDIDropdownRBDMItem } from '@services/hooks/endusers/cmdiDropdownRBDM';
import { useCMDIDropdownRBDMQuery } from '@services/hooks/endusers/useCMDIDropdownRBDMQuery';
import { useRBDMClubDropdownQuery } from '@services/hooks/meetings/useRBDMClubDropdownQuery';
import { useMDPMMeetingMutation } from '@services/hooks/meetings/useMDPMeetingMutation';
import { useMdpMeetingListQuery } from '@services/hooks/meetings/useMdpMeetingListQuery';
import { useAvailableProductsListQuery } from '@services/hooks/site-leads/useAvailableProductsListQuery';
import { MaxLimitMap, MEETING_HIDE_CLUB, SocialMeetSubTypes } from '../../components/addNewMeeting/index';
import { AvailableProductsList } from '@services/hooks/site-leads';
import { AvailableProducts } from '@components/siteLeads/components/siteAddress';
import { useRbdmTownDetailsQuery } from '@services/hooks/meetings/useRbdmTownList';
import { useRbdmCmdiUserCountQuery } from '@services/hooks/meetings/useRbdmCmdiUserCountQuery';

export const AddNewMdpMeeting = (): ReactElement => {
	const { state } = useLocation();
	const { AddMDPMeetingValidationSchema } = useValidation();
	const { mutate, isPending } = useMDPMMeetingMutation();
	const { refetch } = useMdpMeetingListQuery();
	const { setToaster } = useTosterStore();
	const { data, isFetching, error } = useGetMeetingQuery(state?.id);
	useSetMainHeader(state?.id ? data?.name : 'Add New MDP Planning Meet');
	useCMDIDropdownRBDMQuery();
	const { result: cmdiList } = useGetRxDb<CMDIDropdownRBDMItem>('mdi_code_list');

	// State to track selected CMDI for enabling the query
	const [selectedCmdi, setSelectedCmdi] = useState<string | null>(null);

	const [selectedMeetingType, setSelectedMeetingType] = useState<RxDocumentData<MeetingType> | null>(null);
	const [selectedTown, setSelectedTownType] = useState<string | null>(null);

	// Call the query only when selectedCmdi is set (i.e., after dropdown selection)
	const { data: RBDMList, isFetching: isRBDMListFetching } = useRBDMClubDropdownQuery(selectedCmdi || '');
	const { data: cmdiUserCount } = useRbdmCmdiUserCountQuery(selectedCmdi || '');

	// Use different initial values and validation for RBDM users
	const formik = useFormik({
		initialValues: defaultAddMdpMeetingInitialValues,
		onSubmit: () => {
			handleSubmit();
		},
		validationSchema: AddMDPMeetingValidationSchema,
	});

	const handleSubmitClick = (): void => {
		formik.handleSubmit();
	};

	const clubError = formik.touched.cmditownlist_c && formik.errors.cmditownlist_c ? formik.errors.cmditownlist_c : null;

	const navigate = useNavigate();

	useMeetingsTypesQuery();
	const { result: meetingTypes } = useGetRxDb<MeetingType>('meeting_types');
	const meetingTypesFormatted = useMemo(
		() =>
			meetingTypes.map((item) => ({
				id: item.id,
				value: item.name ?? '',
				sfid: item.sfid ?? '',
			})),
		[meetingTypes]
	);

	useIMRCodeListQuery();
	const { data: rbdmTownList } = useRbdmTownDetailsQuery(selectedCmdi || '');

	const isObMeeting = (formik.values.meetingType ?? '').toLowerCase().includes('ob');
	const meetingTypeValue = (formik.values.meetingType ?? '').toLowerCase();
	const shouldHideClub = MEETING_HIDE_CLUB.includes(meetingTypeValue);

	useEffect(() => {
		if (shouldHideClub) {
			const memberCount = cmdiUserCount?.user_count ?? 0;
			formik.setFieldValue('no_of_participants', memberCount);
		}
	}, [formik.values.mdicode_c, formik.values.meetingType, cmdiUserCount]);

	const clubOptionsFormatted = useMemo(() => {
		const clubs = (RBDMList ?? []) as unknown as RBDMClubDropdownItem[];
		return clubs.map((item) => ({
			id: item.sfid ? item.sfid.toString() : item.id.toString(),
			value: item.name ?? '',
			user_count: item.user_count ?? 0,
			ob_user_count: item.ob_user_count ?? 0,
		}));
	}, [RBDMList]);

	const cmdiOptions = useMemo((): DropdownOptions[] => {
		if (cmdiList?.length) {
			return cmdiList?.map((cmdi) => ({
				id: cmdi.sfid,
				value: cmdi.mdicode_c,
			}));
		}
		return [];
	}, [cmdiList]);

	const handleSelectedRBDMClubDropDown = useCallback(
		(selected: DropdownOptions): void => {
			if (!cmdiList) {
				setToaster(true, ToastType.error, 'CMDI list is not available');
				return;
			}
			const selectedCMDI = cmdiList.find((item) => item.mdicode_c === selected.value);

			if (selectedCMDI?.mdicode_c) {
				setSelectedCmdi(selectedCMDI.mdicode_c);
				formik.setFieldValue('assigned_cmdi_c', selectedCMDI.sfid);
			} else {
				setSelectedCmdi(null);
				formik.setFieldValue('cmditownlist_c', []);
				formik.setFieldValue('assigned_cmdi_c', '');
			}
		},
		[cmdiList, formik, setToaster]
	);

	const handleMonthSelection = useCallback(
		(selected: DropdownOptions): void => {
			formik.setFieldValue('monthname_c', selected.value);
		},
		[formik]
	);

	useEffect(() => {
		if (shouldHideClub) {
			return;
		}
		if (formik.values.cmditownlist_c && Array.isArray(formik.values.cmditownlist_c)) {
			const currentValue = formik.values.cmditownlist_c;
			let selectedClubs: DropdownOptions[] = [];

			if (Array.isArray(currentValue)) {
				selectedClubs = [...currentValue];
			} else if (currentValue) {
				selectedClubs = [{ id: currentValue, value: currentValue }];
			}

			const totalUserCount = selectedClubs.reduce((sum, club) => {
				const selectedClub = clubOptionsFormatted.find((item) => item.id === club.id);
				if (isObMeeting) {
					return sum + (selectedClub?.ob_user_count || null);
				}
				return sum + (selectedClub?.user_count || null);
			}, null);

			if (totalUserCount != null) {
				formik.setFieldTouched('no_of_participants', true);
			}
			formik.setFieldValue('no_of_participants', totalUserCount);
		}
	}, [formik.values.cmditownlist_c, clubOptionsFormatted]);

	const handleSubmit = useCallback(async () => {
		const meetingName = formik.values?.mdicode_c ? formik.values?.mdicode_c + Date.now() : '';
		const payload: MDPMeetingDetails = {
			meeting_type_c: formik.values.meetingType,
			name: meetingName,
			assigned_cmdi_c: formik.values.assigned_cmdi_c,
			mdicode_c: formik.values.mdicode_c,
			cmditownlist_c: Array.isArray(formik.values.cmditownlist_c)
				? formik.values.cmditownlist_c.map((item) => item.value).join()
				: formik.values.cmditownlist_c,
			participantspermeeting_c: formik.values.participantspermeeting_c,
			monthname_c: formik.values.monthname_c,
			plan_budget_c: formik.values.plan_budget_c,
			focus_product_1: formik?.values?.focus_product_1 ?? '',
			town_c: formik?.values.town_c ?? '',
			financial_year_c: formik?.values.financial_year_c ?? '',
		};

		payload.end_user_category_c = formik?.values?.meetSubCategory ?? '';

		mutate(payload, {
			onSuccess: () => {
				navigate('/rbdm/meetings/mdp');
				setToaster(true, ToastType.success, 'Meeting Added Successfully');
				refetch?.();
			},
			onError: (error) => {
				setToaster(true, ToastType.error, error.message);
			},
		});
	}, [formik.values, mutate, setToaster, navigate, refetch]);

	const renderClubSelection = (): JSX.Element | null => {
		const maxLimit = MaxLimitMap[formik.values.meetingType ?? ''];

		return (
			<li className="d-col d-col-2">
				<MultiselectDropdown
					id="cmditownlist_c"
					name="cmditownlist_c"
					label="Club"
					onBlur={formik.handleBlur}
					values={Array.isArray(formik.values.cmditownlist_c) ? formik.values.cmditownlist_c : []}
					options={clubOptionsFormatted}
					maxLimit={maxLimit}
					setFieldValue={formik.setFieldValue}
					required
					disabled={!selectedCmdi || isRBDMListFetching}
					className="input-border"
					error={Array.isArray(clubError) ? clubError.join(', ') : clubError}
				/>
			</li>
		);
	};

	const handleMeetingTypeChange = useCallback(
		(e: DropdownOptions): void => {
			const meetType = meetingTypes.find((item) => item.name === e.value);
			const sfid = meetType?._data?.sfid ?? '';
			setSelectedMeetingType(meetType?._data ?? null);

			formik.setFieldValue('meetingSfid', sfid);

			formik.setFieldValue('meetSubCategory', '');
			formik.setFieldValue('cmditownlist_c', []);
			formik.setFieldValue('participantspermeeting_c', '');
			formik.setFieldValue('plan_budget_c', null);
			formik.setFieldValue('per_head_cost', null);
			formik.setFieldValue('focus_product_1', '');
			formik.setFieldValue('no_of_participants', null);
		},
		[meetingTypes, formik]
	);

	useEffect(() => {
		if (formik.values.town_c) {
			setSelectedTownType(formik.values.town_c);
		}
	}, [formik.values.town_c]);

	//Calculate Budget
	useEffect(() => {
		if (selectedMeetingType && selectedTown) {
			const noOfParticipats = parseFloat(formik.values.participantspermeeting_c ?? 0);
			const selectedTownType = rbdmTownList?.find((item) => item.towncode_c === selectedTown);
			const townType = selectedTownType?.townclass_c?.toLowerCase();

			const metroo = parseFloat(selectedMeetingType?.townmetro_c);
			const minimetro = parseFloat(selectedMeetingType.townminimetro_c);
			const classaa = parseFloat(selectedMeetingType.townclassa_c);
			const classbb = parseFloat(selectedMeetingType.townclassb_c);
			const classcc = parseFloat(selectedMeetingType.townclassc_c);

			let budget = 0;
			let perHeadCost = 0;

			switch (townType) {
				case 'metro':
					perHeadCost = metroo;
					budget = metroo * noOfParticipats;
					break;
				case 'metro1':
					perHeadCost = metroo;
					budget = metroo * noOfParticipats;
					break;
				case 'minimetro':
					perHeadCost = minimetro;
					budget = minimetro * noOfParticipats;
					break;
				case 'classa':
					perHeadCost = classaa;
					budget = classaa * noOfParticipats;
					break;
				case 'classb':
					perHeadCost = classbb;
					budget = classbb * noOfParticipats;
					break;
				case 'classc':
					perHeadCost = classcc;
					budget = classcc * noOfParticipats;
					break;
				default:
					perHeadCost = 0;
					budget = 0;
					break;
			}
			formik.setFieldValue('per_head_cost', perHeadCost.toFixed(2));
			if (budget >= 0) {
				formik.setFieldValue('plan_budget_c', budget.toFixed(2));
			}
		}
	}, [
		selectedTown,
		rbdmTownList,
		selectedMeetingType,
		formik.values.no_of_participants,
		formik.values.participantspermeeting_c,
	]);

	const getFinancialYears = (): DropdownOptions[] => {
		const currentDate = new Date();
		const year = currentDate.getFullYear();
		const month = currentDate.getMonth() + 1; // 1-12

		const startYear = month >= 4 ? year : year - 1;

		const currentFinancialYear = `${startYear}-${String(startYear + 1).slice(-2)}`;
		const nextFinancialYear = `${startYear + 1}-${String(startYear + 2).slice(-2)}`;

		return [
			{ id: currentFinancialYear, value: currentFinancialYear },
			{ id: nextFinancialYear, value: nextFinancialYear },
		];
	};

	useAvailableProductsListQuery();

	const { result: availableProductsDataList } = useGetRxDb<AvailableProductsList>('products');
	const availableProductsList = useMemo((): AvailableProducts[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				value: product?.productgroup3description_c,
			}));
		}
		return [];
	}, [availableProductsDataList]);

	return (
		<div className="form-popup show">
			<section className="new-meeting-section new-site-desktop overflow desktop-popup">
				<div className="container">
					<Wrapper isError={error} isLoaderVisible={isFetching}>
						<form>
							<ul className="new-meeting-wrapper row">
								<li className="form-control d-col d-col-2 ">
									<Dropdown
										id="meetingType"
										name="meetingType"
										label="Meeting Type"
										onBlur={formik.handleBlur}
										title={formik.values.meetingType ?? ''}
										error={formik.touched.meetingType && formik.errors.meetingType ? formik.errors.meetingType : null}
										options={meetingTypesFormatted}
										setFieldValue={formik.setFieldValue}
										required
										disabled={state?.id && (formik.values as IDefaultAddMdpMeetingFormInitialValues).rbdm_code}
										onChange={handleMeetingTypeChange}
									/>
								</li>

								{SocialMeetType.includes(formik.values?.meetingType) && (
									<li className="d-col d-col-2  ">
										<Dropdown
											id="meetSubCategory"
											name="meetSubCategory"
											label="Social Meet Sub-Type"
											onBlur={formik.handleBlur}
											title={formik.values.meetSubCategory ?? ''}
											error={
												formik.touched.meetSubCategory && formik.errors.meetSubCategory
													? formik.errors.meetSubCategory
													: null
											}
											options={SocialMeetSubTypes}
											setFieldValue={formik.setFieldValue}
											required
										/>
									</li>
								)}

								<li className="d-col d-col-2">
									<Dropdown
										id="mdicode_c"
										name="mdicode_c"
										label="BDE"
										onBlur={formik.handleBlur}
										title={formik.values.mdicode_c ?? ''}
										error={formik.touched.mdicode_c && formik.errors.mdicode_c ? formik.errors.mdicode_c : null}
										options={cmdiOptions}
										setFieldValue={formik.setFieldValue}
										required
										onChange={handleSelectedRBDMClubDropDown}
									/>
								</li>

								<li className="d-col d-col-2">
									<Dropdown
										id="financial_year_c"
										name="financial_year_c"
										label="Year"
										onBlur={formik.handleBlur}
										title={formik.values.financial_year_c ?? ''}
										error={
											formik.touched.financial_year_c && formik.errors.financial_year_c
												? formik.errors.financial_year_c
												: null
										}
										options={getFinancialYears()}
										setFieldValue={formik.setFieldValue}
										required
										onChange={(e: DropdownOptions) => formik.setFieldValue('financial_year_c', e.value)}
									/>
								</li>

								<li className="d-col d-col-2  ">
									<Dropdown
										id="monthname_c"
										name="monthname_c"
										label="Month"
										onBlur={formik.handleBlur}
										title={formik.values.monthname_c ?? ''}
										error={formik.touched.monthname_c && formik.errors.monthname_c ? formik.errors.monthname_c : null}
										options={monthsOptions()}
										setFieldValue={formik.setFieldValue}
										required
										onChange={handleMonthSelection}
									/>
								</li>

								{!shouldHideClub && renderClubSelection()}
								{isObMeeting ? (
									<li className="form-control d-col d-col-2">
										<TextInput
											id="no_of_participants"
											required
											name="no_of_participants"
											type="number"
											value={formik.values.no_of_participants ?? ''}
											label="Number Of OB Member Available"
											disabled={true}
											error={
												formik.touched.no_of_participants && formik.errors.no_of_participants
													? formik.errors.no_of_participants
													: null
											}
										/>
									</li>
								) : (
									<li className="form-control d-col d-col-2">
										<TextInput
											id="no_of_participants"
											required
											name="no_of_participants"
											type="number"
											value={formik.values.no_of_participants ?? ''}
											label="Number Of FCC Member Available"
											disabled={true}
											error={
												formik.touched.no_of_participants && formik.errors.no_of_participants
													? formik.errors.no_of_participants
													: null
											}
										/>
									</li>
								)}

								<li className="d-col d-col-2">
									<Dropdown
										id="town"
										name="town_c"
										label="Town"
										onBlur={formik.handleBlur}
										title={
											rbdmTownList?.find(
												(t) => t.towncode_c === (formik.values as IDefaultAddMdpMeetingFormInitialValues).town_c
											)
												? `${(formik.values as IDefaultAddMdpMeetingFormInitialValues).town_c} - ${
														rbdmTownList?.find(
															(t) => t.towncode_c === (formik.values as IDefaultAddMdpMeetingFormInitialValues).town_c
														)?.towndescription_c
													}`
												: ''
										}
										error={formik.touched.town_c && formik.errors.town_c ? formik.errors.town_c : null}
										options={
											rbdmTownList
												?.filter((t) => t.towncode_c && t.towndescription_c)
												.map((t) => ({
													id: t.towncode_c as string,
													value: `${t.towncode_c} - ${t.towndescription_c}`,
												})) ?? []
										}
										setFieldValue={(field: string, value: string, shouldValidate?: boolean) => {
											const selectedOption = rbdmTownList?.find(
												(t) => `${t.towncode_c} - ${t.towndescription_c}` === value
											);
											formik.setFieldValue(field, selectedOption?.towncode_c ?? '', shouldValidate);
										}}
										required
										disabled={!selectedCmdi}
										placeholder={selectedCmdi ? 'Select Town' : 'Select CMDI first'}
									/>
								</li>

								<li className="form-control d-col d-col-2">
									<TextInput
										id="per_head_cost"
										required
										name="per_head_cost"
										type="number"
										onKeyDown={keyDownEvent}
										onBlur={formik.handleBlur}
										value={formik.values.per_head_cost ?? null}
										setFieldValue={formik.setFieldValue}
										label="Per Head Cost"
										error={
											formik.touched.per_head_cost && formik.errors.per_head_cost ? formik.errors.per_head_cost : null
										}
										disabled={true}
									/>
								</li>

								<li className="form-control d-col d-col-2">
									<TextInput
										id="participantspermeeting_c"
										required
										name="participantspermeeting_c"
										type="number"
										onKeyDown={keyDownEvent}
										onBlur={formik.handleBlur}
										value={formik.values.participantspermeeting_c ?? null}
										setFieldValue={formik.setFieldValue}
										label="Number Of Participants"
										error={
											formik.touched.participantspermeeting_c && formik.errors.participantspermeeting_c
												? formik.errors.participantspermeeting_c
												: null
										}
									/>
								</li>

								<li className="form-control d-col d-col-2">
									<TextInput
										required
										id="plan_budget_c"
										name="plan_budget_c"
										type="text"
										onBlur={formik.handleBlur}
										value={formik.values.plan_budget_c ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Meeting Budget"
										disabled={true}
										error={
											formik.touched.plan_budget_c && formik.errors.plan_budget_c ? formik.errors.plan_budget_c : null
										}
									/>
								</li>

								{/* Products Fields - Only for non-RBDM users */}
								{PRODUCT_SPECIFIC_MEETS.includes((formik.values.meetingType ?? '').toLowerCase()) && (
									<li className="d-col d-col-2  ">
										<Dropdown
											id="focus_product_1"
											name="focus_product_1"
											label="Primary Focus Product"
											onBlur={formik.handleBlur}
											title={formik.values.focus_product_1 ?? ''}
											error={
												formik.touched.focus_product_1 && formik.errors.focus_product_1
													? formik.errors.focus_product_1
													: null
											}
											options={availableProductsList}
											setFieldValue={formik.setFieldValue}
											required
										/>
									</li>
								)}
							</ul>
							<SubmitButton
								label="Submit"
								isDisabled={!formik.isValid}
								isEditing={false}
								handleSubmitClick={handleSubmitClick}
								isPending={isPending}
							/>
						</form>
					</Wrapper>
				</div>
			</section>
		</div>
	);
};
